import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  ChefHat,
  HeartPulse,
  MapPin,
  MessageCircle,
  Phone,
  ShieldCheck,
  Sprout,
  UtensilsCrossed,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { Section } from "@/components/Section";
import { PageHero } from "@/components/PageHero";
import { ValueCard } from "@/components/ValueCard";
import { WhatsAppActions } from "@/components/WhatsAppActions";
import { DeliveryAreaCheck } from "@/components/DeliveryAreaCheck";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { buildTelUrl, buildWhatsappUrl } from "@/lib/whatsapp";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { useDeliveryAreas } from "@/hooks/useDeliveryAreas";
import { trackCtaClick } from "@/lib/analytics";
import { CmsImageBanner } from "@/components/CmsImageBanner";
import { CmsCtaLink } from "@/components/CmsCtaLink";
import { useContactContent, cmsImage, cmsText } from "@/hooks/usePageContent";
import { submitContactEnquiry } from "@/lib/submissions/contact.functions";
import {
  composePlanEnquiryLines,
  enquiryLabelForParam,
  isPlanEnquiryType,
  parseEnquiryParam,
  type EnquiryParamValue,
} from "@/lib/submissions/enquiry-types";
import { SITE_URL, canonicalUrl } from "@/lib/seo/site";

const CANONICAL = canonicalUrl("/contact");

const META_TITLE =
  "Contact & Mumbai Delivery Areas | Poonam Kapur Healthy Kitchen";
const META_DESCRIPTION =
  "Ask Poonam Kapur Healthy Kitchen about meal plans and healthy meal delivery in Mumbai. Confirm your neighbourhood, send an enquiry or chat on WhatsApp.";

export const Route = createFileRoute("/contact")({
  // ?enquiry=corporate-meal-plan|family-feast preselects the enquiry type
  // (used by the enquiry-only meal plan CTAs). Anything else is ignored.
  validateSearch: (search: Record<string, unknown>): { enquiry?: EnquiryParamValue } => ({
    enquiry: parseEnquiryParam(search.enquiry) ?? undefined,
  }),
  head: () => ({
    meta: [
      { title: META_TITLE },
      { name: "description", content: META_DESCRIPTION },
      { property: "og:title", content: META_TITLE },
      { property: "og:description", content: META_DESCRIPTION },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: META_TITLE },
      { name: "twitter:description", content: META_DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: META_TITLE,
          url: CANONICAL,
          about: {
            "@type": "Organization",
            name: "Poonam Kapur Healthy Kitchen",
            url: SITE_URL,
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+91 97550 20183",
              contactType: "customer service",
              areaServed: "Mumbai",
            },
          },
        }),
      },
    ],
  }),
  component: ContactPage,
});

const MEAL_INTERESTS = [
  "Breakfast",
  "Lunch",
  "Snacks",
  "Dinner",
  "Full-day",
  "Corporate Meal Plan",
  "Family Feast",
  "Not sure yet",
] as const;

const COVERAGE_AREAS = [
  "Andheri",
  "Bandra",
  "BKC",
  "Juhu",
  "Powai",
  "Worli",
];

const FAQ = [
  {
    q: "Which areas do you deliver to?",
    a: "We deliver across 70+ Mumbai neighbourhoods including Andheri, Bandra, BKC, Juhu, Powai and Worli. Message us on WhatsApp to confirm delivery to your exact location.",
  },
  {
    q: "What meal timings do you offer?",
    a: "Four slots daily: Breakfast, Lunch, Snacks and Dinner. Each slot is prepared and dispatched fresh for that window.",
  },
  {
    q: "What are the order cutoff times?",
    a: "Breakfast — order by 8:00 pm the previous day. Lunch — 6:00 am same day. Snacks — 8:30 pm previous day. Dinner — 12:01 pm same day.",
  },
  {
    q: "How do you ensure freshness?",
    a: "Meals are cooked to the slot they're delivered in — never reheated batches. Farm-direct ingredients and short cook-to-delivery windows keep meals fresh.",
  },
  {
    q: "What kind of packaging do you use?",
    a: "Food-grade, hygienic packaging designed for safe, fresh, in-city delivery.",
  },
];

const TRUST = [
  {
    icon: ShieldCheck,
    title: "Hygienic kitchens",
    body: "Meals are prepared in kitchens run to strict hygiene standards, every step of the way.",
  },
  {
    icon: UtensilsCrossed,
    title: "Separate Veg & Non-Veg kitchens",
    body: "Fully separated prep areas so vegetarian meals stay vegetarian — no shared surfaces or equipment.",
  },
  {
    icon: Sprout,
    title: "Farm-direct sourcing",
    body: "Traceable, seasonal ingredients from growers we know — never long-shelf produce.",
  },
  {
    icon: HeartPulse,
    title: "Nutrition-first",
    body: "Menus designed around balance, not shortcuts — the same principle across every meal.",
  },
  {
    icon: ChefHat,
    title: "Fresh preparation",
    body: "Cooked to the slot they're delivered in. No reheated batches, no compromises.",
  },
];

type FieldName =
  | "name"
  | "mobile"
  | "email"
  | "location"
  | "mealInterest"
  | "peopleCount"
  | "preferredDate"
  | "preferredTime"
  | "dietaryPreferences"
  | "message";

type FormValues = Record<FieldName, string>;

type FormErrors = Partial<Record<FieldName, string>>;

const INITIAL_VALUES: FormValues = {
  name: "",
  mobile: "",
  email: "",
  location: "",
  mealInterest: "",
  peopleCount: "",
  preferredDate: "",
  preferredTime: "",
  dietaryPreferences: "",
  message: "",
};

function validate(values: FormValues): FormErrors {
  const errors: FormErrors = {};
  const name = values.name.trim();
  if (name.length < 2) errors.name = "Please enter your name (at least 2 characters).";
  else if (name.length > 80) errors.name = "Name must be 80 characters or fewer.";

  const mobileDigits = values.mobile.replace(/\D/g, "");
  if (!values.mobile.trim()) errors.mobile = "Please enter a mobile number.";
  else if (mobileDigits.length < 10 || mobileDigits.length > 15)
    errors.mobile = "Enter a valid mobile number (10–15 digits).";

  const email = values.email.trim();
  if (!email) errors.email = "Please enter an email address.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
    errors.email = "Enter a valid email address.";

  if (!values.location.trim()) errors.location = "Please enter your location.";

  if (!values.mealInterest) errors.mealInterest = "Please choose a meal interest.";

  // Optional plan-enquiry details (Corporate Meal Plan / Family Feast).
  if (values.peopleCount.trim() && !/^\d{1,5}$/.test(values.peopleCount.trim()))
    errors.peopleCount = "Enter a number (e.g. 25).";
  if (values.preferredTime.length > 40)
    errors.preferredTime = "Preferred time must be 40 characters or fewer.";
  if (values.dietaryPreferences.length > 500)
    errors.dietaryPreferences = "Dietary preferences must be 500 characters or fewer.";

  if (values.message.length > 1000)
    errors.message = "Message must be 1000 characters or fewer.";

  return errors;
}

function buildEnquiryMessage(values: FormValues): string {
  return [
    "Hi Poonam Kapur Healthy Kitchen, I'd like to enquire about your meals.",
    "",
    `Name: ${values.name.trim()}`,
    `Mobile: ${values.mobile.trim()}`,
    `Email: ${values.email.trim()}`,
    `Location: ${values.location.trim()}`,
    `Meal Interest: ${values.mealInterest}`,
    ...composePlanEnquiryLines(values),
    `Message: ${values.message.trim() || "—"}`,
  ].join("\n");
}

const inputClass =
  "w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 outline-none transition-colors focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30 aria-[invalid=true]:border-destructive";

function ContactPage() {
  const config = useSiteConfig();
  const deliveryAreas = useDeliveryAreas();
  const serviceAreaText = (() => {
    if (deliveryAreas.isLoading || deliveryAreas.isFallback) {
      return "Mumbai — 70+ neighbourhoods including Andheri, Bandra, BKC, Juhu, Powai and Worli.";
    }
    if (deliveryAreas.isEmpty) {
      return "Mumbai — service areas are being updated.";
    }
    const names = deliveryAreas.areas.slice(0, 6).map((a) => a.neighbourhood);
    const joined =
      names.length <= 1
        ? names.join("")
        : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
    if (deliveryAreas.areas.length > 6) {
      return `Mumbai — 70+ neighbourhoods including ${joined} and more.`;
    }
    return `Mumbai — ${joined}.`;
  })();
  const telUrl = buildTelUrl(config.telNumber || config.whatsappNumber);
  const displayNumber = config.whatsappNumberDisplay || config.whatsappNumber;
  const waUrlDefault = buildWhatsappUrl(config.whatsappNumber, config.whatsappDefaultMessage);
  const buildWa = (msg: string) => buildWhatsappUrl(config.whatsappNumber, msg);
  const { enquiry } = Route.useSearch();
  const preselectedInterest = enquiry ? enquiryLabelForParam(enquiry) : "";
  const [values, setValues] = useState<FormValues>(() => ({
    ...INITIAL_VALUES,
    mealInterest: preselectedInterest,
  }));
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [handoff, setHandoff] = useState<{ url: string } | null>(null);
  const [honeypot, setHoneypot] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submit = useServerFn(submitContactEnquiry);

  const setField = (field: FieldName, value: string) => {
    setValues((v) => ({ ...v, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    const validation = validate(values);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    setSubmitting(true);
    setErrors({});
    setSubmitError(null);
    try {
      const result = await submit({
        data: {
          name: values.name.trim(),
          mobile: values.mobile.trim(),
          email: values.email.trim(),
          location: values.location.trim(),
          mealInterest: values.mealInterest,
          peopleCount: values.peopleCount.trim(),
          preferredDate: values.preferredDate.trim(),
          preferredTime: values.preferredTime.trim(),
          dietaryPreferences: values.dietaryPreferences.trim(),
          message: values.message.trim(),
          honeypot,
        },
      });
      const url = buildWa(buildEnquiryMessage(values));
      if (result.stored) {
        trackCtaClick({
          action: "contact_form_saved",
          location: "contact",
          label: "Contact enquiry saved",
        });
      }
      setHandoff({ url });
    } catch (err) {
      console.error("[contact] submit failed", err);
      setSubmitError(
        "We couldn't save your enquiry right now. Please try again or contact us on WhatsApp.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const continueOnWhatsApp = () => {
    if (!handoff) return;
    if (typeof window === "undefined") return;
    const win = window.open(handoff.url, "_blank", "noopener,noreferrer");
    if (win) {
      trackCtaClick({
        action: "contact_form_whatsapp_handoff",
        location: "contact",
        label: "Continue on WhatsApp",
        href: handoff.url,
      });
    }
  };

  const startOver = () => {
    setValues({ ...INITIAL_VALUES, mealInterest: preselectedInterest });
    setErrors({});
    setHandoff(null);
    setHoneypot("");
    setSubmitError(null);
  };

  // Arriving from an enquiry-only plan CTA: bring the form into view.
  useEffect(() => {
    if (!enquiry) return;
    document
      .getElementById("contact-enquiry-form")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [enquiry]);

  const err = (f: FieldName) => errors[f];
  const cms = useContactContent();

  return (
    <SiteLayout>
      <PageHero
        eyebrow={cmsText(cms.hero_eyebrow, "Contact")}
        title={cmsText(cms.hero_title, "Let's start your healthy eating journey.")}
        intro={cmsText(
          cms.hero_intro,
          "Questions about a meal plan, delivery in your neighbourhood or a corporate order? Message us on WhatsApp or send an enquiry below — the team will reply personally.",
        )}
      />

      {cmsImage(cms.hero_image_url) ? (
        <Section className="pb-0">
          <CmsImageBanner
            url={cms.hero_image_url}
            alt="Contact Healthy Kitchen"
            caption={cms.hero_image_caption}
          />
        </Section>
      ) : null}

      {/* 2. Contact Information */}
      <Section>
        <CmsImageBanner
          url={cms.office_image_url}
          alt="Our kitchen"
          caption={cms.office_image_caption}
          className="mb-10"
        />
        <div className="max-w-2xl">
          <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {cmsText(cms.info_eyebrow, "How to reach us")}
          </span>
          <h2 className="mt-3 font-display text-3xl font-medium md:text-4xl">
            {cmsText(cms.info_heading, "Real people, quick replies.")}
          </h2>
          {cms.info_description ? (
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              {cms.info_description}
            </p>
          ) : null}
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-7">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-secondary/60 text-primary ring-1 ring-border">
              <MessageCircle size={18} strokeWidth={1.75} aria-hidden />
            </div>
            <h3 className="mt-5 font-display text-xl">WhatsApp</h3>
            <dl className="mt-4 space-y-3 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  Number
                </dt>
                <dd className="mt-1">
                  <span className="text-foreground">
                    {displayNumber}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  WhatsApp
                </dt>
                <dd className="mt-1">
                  <a
                    href={waUrlDefault}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-foreground underline-offset-4 hover:underline"
                    onClick={() =>
                      trackCtaClick({
                        action: "whatsapp_chat",
                        location: "contact-info",
                        href: waUrlDefault,
                      })
                    }
                  >
                    Chat on WhatsApp
                  </a>
                </dd>
              </div>
            </dl>
          </div>

          <div className="rounded-2xl border border-border bg-card p-7">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-secondary/60 text-primary ring-1 ring-border">
              <MapPin size={18} strokeWidth={1.75} aria-hidden />
            </div>
            <h3 className="mt-5 font-display text-xl">Service Area</h3>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              {serviceAreaText}
            </p>
          </div>
        </div>
      </Section>

      {/* 3. Contact Form — WhatsApp handoff */}
      <Section className="bg-secondary/30" id="contact-enquiry-form">
        <CmsImageBanner
          url={cms.illustration_image_url}
          alt="Send us an enquiry"
          caption={cms.illustration_image_caption}
          className="mb-12"
        />
        <div className="grid gap-12 md:grid-cols-[1fr_1.2fr] md:items-start md:gap-16">
          <div>
            <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {cmsText(cms.form_eyebrow, "Enquiry")}
            </span>
            <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">
              {cmsText(cms.form_heading, "Send us the details.")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              {cmsText(
                cms.form_description,
                "Fill in a few details and we'll open WhatsApp with your enquiry pre-filled. Send it from WhatsApp so the team receives your message directly.",
              )}
            </p>
          </div>


          {handoff ? (
            <div
              role="status"
              aria-live="polite"
              className="rounded-3xl border border-border bg-card p-6 md:p-8"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-primary">
                Ready to send
              </div>
              <h3 className="mt-3 font-display text-2xl font-medium">
                Thanks — your enquiry has been received.
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                For the fastest reply, continue the conversation on WhatsApp. You can
                also call or copy our number below.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={continueOnWhatsApp}
                  className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 text-sm font-medium text-accent-foreground shadow-sm transition-transform duration-300 hover:-translate-y-0.5 motion-reduce:transform-none"
                >
                  <MessageCircle
                    className="h-4 w-4"
                    aria-hidden
                    strokeWidth={1.75}
                  />
                  Continue on WhatsApp
                </button>
                <button
                  type="button"
                  onClick={startOver}
                  className="inline-flex items-center rounded-full border border-border px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                >
                  Start a new enquiry
                </button>
              </div>
              <div className="mt-6 border-t border-border pt-6">
                <WhatsAppActions
                  location="contact-handoff-fallback"
                  message={buildEnquiryMessage(values)}
                  chatLabel="Chat on WhatsApp"
                />
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              noValidate
              className="rounded-3xl border border-border bg-card p-6 md:p-8"
            >
              <div className="grid gap-5">
                {/* Honeypot: hidden from real users; bots often fill every field. */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-10000px",
                    width: "1px",
                    height: "1px",
                    overflow: "hidden",
                  }}
                >
                  <label htmlFor="c-company">Company (leave blank)</label>
                  <input
                    id="c-company"
                    name="company"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <label htmlFor="c-name" className="text-sm font-medium">
                    Name <span aria-hidden className="text-destructive">*</span>
                  </label>
                  <input
                    id="c-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    minLength={2}
                    maxLength={80}
                    value={values.name}
                    onChange={(e) => setField("name", e.target.value)}
                    aria-invalid={!!err("name")}
                    aria-describedby={err("name") ? "c-name-err" : undefined}
                    className={inputClass}
                  />
                  {err("name") && (
                    <p id="c-name-err" className="text-xs text-destructive">
                      {err("name")}
                    </p>
                  )}
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <label htmlFor="c-mobile" className="text-sm font-medium">
                      Mobile Number <span aria-hidden className="text-destructive">*</span>
                    </label>
                    <input
                      id="c-mobile"
                      name="mobile"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      required
                      placeholder="+91 …"
                      value={values.mobile}
                      onChange={(e) => setField("mobile", e.target.value)}
                      aria-invalid={!!err("mobile")}
                      aria-describedby={err("mobile") ? "c-mobile-err" : undefined}
                      className={inputClass}
                    />
                    {err("mobile") && (
                      <p id="c-mobile-err" className="text-xs text-destructive">
                        {err("mobile")}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <label htmlFor="c-email" className="text-sm font-medium">
                      Email <span aria-hidden className="text-destructive">*</span>
                    </label>
                    <input
                      id="c-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      placeholder="you@example.com"
                      value={values.email}
                      onChange={(e) => setField("email", e.target.value)}
                      aria-invalid={!!err("email")}
                      aria-describedby={err("email") ? "c-email-err" : undefined}
                      className={inputClass}
                    />
                    {err("email") && (
                      <p id="c-email-err" className="text-xs text-destructive">
                        {err("email")}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <label htmlFor="c-location" className="text-sm font-medium">
                      Location <span aria-hidden className="text-destructive">*</span>
                    </label>
                    <input
                      id="c-location"
                      name="location"
                      type="text"
                      autoComplete="address-level2"
                      required
                      placeholder="Neighbourhood in Mumbai"
                      value={values.location}
                      onChange={(e) => setField("location", e.target.value)}
                      aria-invalid={!!err("location")}
                      aria-describedby={err("location") ? "c-location-err" : undefined}
                      className={inputClass}
                    />
                    {err("location") && (
                      <p id="c-location-err" className="text-xs text-destructive">
                        {err("location")}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <label htmlFor="c-interest" className="text-sm font-medium">
                      Meal Interest <span aria-hidden className="text-destructive">*</span>
                    </label>
                    <select
                      id="c-interest"
                      name="mealInterest"
                      required
                      value={values.mealInterest}
                      onChange={(e) => setField("mealInterest", e.target.value)}
                      aria-invalid={!!err("mealInterest")}
                      aria-describedby={
                        err("mealInterest") ? "c-interest-err" : undefined
                      }
                      className={inputClass}
                    >
                      <option value="">Select an option…</option>
                      {MEAL_INTERESTS.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                    {err("mealInterest") && (
                      <p id="c-interest-err" className="text-xs text-destructive">
                        {err("mealInterest")}
                      </p>
                    )}
                  </div>
                </div>

                {isPlanEnquiryType(values.mealInterest) && (
                  <>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div className="grid gap-2">
                        <label htmlFor="c-people" className="text-sm font-medium">
                          Number of people / meals
                        </label>
                        <input
                          id="c-people"
                          name="peopleCount"
                          type="text"
                          inputMode="numeric"
                          placeholder="e.g. 25"
                          value={values.peopleCount}
                          onChange={(e) => setField("peopleCount", e.target.value)}
                          aria-invalid={!!err("peopleCount")}
                          aria-describedby={err("peopleCount") ? "c-people-err" : undefined}
                          className={inputClass}
                        />
                        {err("peopleCount") && (
                          <p id="c-people-err" className="text-xs text-destructive">
                            {err("peopleCount")}
                          </p>
                        )}
                      </div>

                      <div className="grid gap-2">
                        <label htmlFor="c-date" className="text-sm font-medium">
                          Preferred date
                        </label>
                        <input
                          id="c-date"
                          name="preferredDate"
                          type="date"
                          value={values.preferredDate}
                          onChange={(e) => setField("preferredDate", e.target.value)}
                          className={inputClass}
                        />
                      </div>
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <div className="grid gap-2">
                        <label htmlFor="c-time" className="text-sm font-medium">
                          Preferred delivery time
                        </label>
                        <input
                          id="c-time"
                          name="preferredTime"
                          type="text"
                          maxLength={40}
                          placeholder="e.g. Lunchtime, 12–1 pm"
                          value={values.preferredTime}
                          onChange={(e) => setField("preferredTime", e.target.value)}
                          aria-invalid={!!err("preferredTime")}
                          aria-describedby={err("preferredTime") ? "c-time-err" : undefined}
                          className={inputClass}
                        />
                        {err("preferredTime") && (
                          <p id="c-time-err" className="text-xs text-destructive">
                            {err("preferredTime")}
                          </p>
                        )}
                      </div>

                      <div className="grid gap-2">
                        <label htmlFor="c-dietary" className="text-sm font-medium">
                          Dietary preferences / special requirements
                        </label>
                        <input
                          id="c-dietary"
                          name="dietaryPreferences"
                          type="text"
                          maxLength={500}
                          placeholder="e.g. Jain, no onion-garlic, diabetic-friendly"
                          value={values.dietaryPreferences}
                          onChange={(e) => setField("dietaryPreferences", e.target.value)}
                          aria-invalid={!!err("dietaryPreferences")}
                          aria-describedby={err("dietaryPreferences") ? "c-dietary-err" : undefined}
                          className={inputClass}
                        />
                        {err("dietaryPreferences") && (
                          <p id="c-dietary-err" className="text-xs text-destructive">
                            {err("dietaryPreferences")}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}

                <div className="grid gap-2">
                  <label htmlFor="c-message" className="text-sm font-medium">
                    Message
                  </label>
                  <textarea
                    id="c-message"
                    name="message"
                    rows={4}
                    maxLength={1000}
                    placeholder="Anything else we should know?"
                    value={values.message}
                    onChange={(e) => setField("message", e.target.value)}
                    aria-invalid={!!err("message")}
                    aria-describedby="c-message-help"
                    className={inputClass}
                  />
                  <p
                    id="c-message-help"
                    className="text-xs text-muted-foreground"
                  >
                    Optional — up to 1000 characters. {values.message.length}/1000
                    {err("message") ? ` — ${err("message")}` : ""}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  aria-disabled={submitting}
                  className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow-sm transition-transform duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70 motion-reduce:transform-none"
                >
                  <MessageCircle className="h-4 w-4" aria-hidden strokeWidth={1.75} />
                  {submitting ? "Sending…" : "Send enquiry"}
                </button>
                <div role="status" aria-live="polite" className="min-h-[1rem]">
                  {submitError && (
                    <p className="text-xs text-destructive">{submitError}</p>
                  )}
                </div>
              </div>
            </form>
          )}
        </div>
      </Section>

      {/* 4. WhatsApp Contact */}
      <Section>
        <div className="rounded-3xl border border-border bg-card p-8 md:p-12">
          <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-primary">
                Fastest reply
              </div>
              <h2 className="mt-3 font-display text-3xl font-medium md:text-4xl">
                Prefer WhatsApp? So do we.
              </h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
                Message {displayNumber} — we usually reply the same day.
              </p>
            </div>
            <WhatsAppActions location="contact-whatsapp-card" />
          </div>
        </div>
      </Section>

      {/* 5. Delivery Coverage */}
      <Section className="bg-secondary/30">
        <CmsImageBanner
          url={cms.map_image_url}
          alt="Our delivery area"
          caption={cms.map_image_caption}
          className="mb-10"
        />
        <div className="grid gap-10 md:grid-cols-[1fr_1.2fr] md:items-start md:gap-16">
          <div>
            <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Delivery
            </span>
            <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">
              {cmsText(cms.delivery_heading, "Mumbai-wide, delivered fresh.")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              {cmsText(
                cms.delivery_description,
                "We deliver across 70+ Mumbai neighbourhoods with short cook-to-doorstep windows so meals arrive the way they left the kitchen.",
              )}
            </p>
          </div>

          <div className="rounded-3xl border border-border bg-card p-7 md:p-9">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Neighbourhoods we already serve
            </div>
            <ul className="mt-4 flex flex-wrap gap-2" aria-label="Sample delivery areas">
              {COVERAGE_AREAS.map((area) => (
                <li key={area}>
                  <span className="inline-flex items-center rounded-full border border-border bg-secondary/60 px-4 py-1.5 text-xs font-medium text-foreground">
                    {area}
                  </span>
                </li>
              ))}
              <li>
                <span className="inline-flex items-center rounded-full border border-border bg-secondary/60 px-4 py-1.5 text-xs font-medium text-muted-foreground">
                  and 60+ more
                </span>
              </li>
            </ul>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              For healthy meal delivery in Mumbai, confirm your exact location before ordering. Explore our{" "}
              <Link to="/meal-plans" className="text-primary underline underline-offset-4">meal plans</Link>{" "}
              and <Link to="/weekly-menu" className="text-primary underline underline-offset-4">weekly menu</Link>{" "}
              for current meal options.
            </p>
            <DeliveryAreaCheck />
          </div>
        </div>
      </Section>

      {/* 6. FAQ */}
      <Section>
        <div className="grid gap-12 md:grid-cols-[1fr_1.5fr] md:gap-20">
          <div>
            <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              FAQ
            </span>
            <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">
              {cmsText(cms.faq_heading, "Common questions.")}
            </h2>
            <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
              {cmsText(
                cms.faq_intro,
                "Can't find what you're looking for? Message us on WhatsApp and we'll reply personally.",
              )}
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Visit our <Link to="/faq" className="text-primary underline underline-offset-4">meal and delivery FAQs</Link>{" "}
              for more answers before you enquire.
            </p>
          </div>


          <Accordion type="single" collapsible className="w-full space-y-2">
            {FAQ.map((f, i) => (
              <AccordionItem
                key={i}
                value={`c-faq-${i}`}
                className="rounded-2xl border border-border bg-card px-5 transition-colors data-[state=open]:border-primary/30"
              >
                <AccordionTrigger className="py-5 text-left font-display text-lg font-medium tracking-tight hover:no-underline md:text-xl">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-muted-foreground">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* 7. Trust */}
      <Section className="bg-secondary/30">
        <div className="max-w-2xl">
          <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Why choose us
          </span>
          <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">
            The standards behind every meal.
          </h2>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {TRUST.map((t) => (
            <ValueCard key={t.title} icon={t.icon} title={t.title} body={t.body} />
          ))}
        </div>
      </Section>

      {/* 8. Final CTA */}
      <Section className="pb-24">
        <CmsImageBanner
          url={cms.cta_image_url}
          alt="Healthy Kitchen meals"
          caption={cms.cta_image_caption}
          className="mb-10"
        />
        <div className="relative overflow-hidden rounded-[2rem] bg-primary px-10 py-20 text-primary-foreground md:px-16 md:py-28">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,color-mix(in_oklab,var(--accent)_35%,transparent),transparent_60%)]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,color-mix(in_oklab,var(--primary-foreground)_10%,transparent),transparent_55%)]"
          />
          <div className="relative max-w-2xl">
            <div className="mb-4 h-px w-10 bg-primary-foreground/50" aria-hidden />
            <span className="text-xs uppercase tracking-[0.2em] text-primary-foreground/80">
              {cmsText(cms.cta_eyebrow, "Get started")}
            </span>
            <h2 className="mt-4 font-display text-4xl font-medium leading-tight md:text-6xl">
              {cmsText(cms.cta_heading, "Ready to eat healthier?")}
            </h2>
            <p className="mt-5 max-w-xl text-primary-foreground/90">
              {cmsText(
                cms.cta_description,
                "Order on WhatsApp or take a look at the plans and pick what fits your day.",
              )}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <WhatsAppActions
                location="contact-final-cta"
                tone="dark"
                compact
                chatLabel={cmsText(cms.cta_secondary_text, "Order on WhatsApp")}
              />
              <CmsCtaLink
                href={cmsText(cms.cta_primary_link, "/meal-plans")}
                onClick={() =>
                  trackCtaClick({
                    action: "view_meal_plans",
                    location: "contact-cta",
                    href: "/meal-plans",
                  })
                }
                className="inline-flex items-center rounded-full border border-primary-foreground/40 bg-primary-foreground/5 px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-foreground/10"
              >
                {cmsText(cms.cta_primary_text, "View Meal Plans")}
              </CmsCtaLink>
            </div>
          </div>
        </div>
      </Section>

    </SiteLayout>
  );
}